import { Controller } from "@hotwired/stimulus"

// Opens a modal with the history chart for whichever stat card was clicked. The chart itself
// is a component fetched on demand, so the stats page doesn't pay for five histories up front.
export default class extends Controller {
  static targets = ["modal", "title", "body"]
  static values = { chartPath: String }

  // Matches the stat-card open animation in see_through_stat.css.
  static OPEN_ANIMATION_MS = 550

  open(event) {
    const { stat, label } = event.params
    if (!stat) return

    this.flash(event.currentTarget)
    this.requestedStat = stat
    this.titleTarget.textContent = label || "Stat History"
    this.bodyTarget.innerHTML = this.wrap('<div class="chart-frame chart-loading"><div class="loading-dots"></div></div>')
    this.shown = this.whenShown()
    this.modal.show()
    this.load(stat)
  }

  // A fading modal only gets its size once the backdrop transition finishes, so this is what
  // the chart waits on before it is inserted.
  whenShown() {
    if (this.modalTarget.classList.contains("show")) return Promise.resolve()

    return new Promise(resolve => {
      this.modalTarget.addEventListener("shown.bs.modal", resolve, { once: true })
    })
  }

  // Plays the press animation on the clicked card, restarting it if it is already running.
  flash(card) {
    clearTimeout(this.flashTimeout)
    this.flashedCard?.classList.remove("is-opening")

    card.classList.remove("is-opening")
    void card.offsetWidth
    card.classList.add("is-opening")

    this.flashedCard = card
    this.flashTimeout = setTimeout(() => card.classList.remove("is-opening"), this.constructor.OPEN_ANIMATION_MS)
  }

  clear() {
    this.requestedStat = null
    this.bodyTarget.innerHTML = ""
  }

  async load(stat) {
    try {
      const response = await fetch(`${this.chartPathValue}?stat=${encodeURIComponent(stat)}`, {
        headers: { Accept: "text/html" }
      })
      if (!response.ok) throw new Error(`Chart request failed with status ${response.status}`)

      const html = await response.text()
      // ApexCharts measures its container as it renders, so a chart inserted into a modal that
      // is still transitioning in would be drawn at zero size and stay that way.
      await this.shown
      if (this.requestedStat !== stat) return

      this.bodyTarget.innerHTML = html
    } catch (error) {
      console.warn("Error loading SeeThrough stat chart:", error)
      if (this.requestedStat !== stat) return

      this.bodyTarget.innerHTML = this.wrap('<p class="text-center mb-0">This history could not be loaded.</p>')
    }
  }

  get modal() {
    return window.bootstrap.Modal.getOrCreateInstance(this.modalTarget)
  }

  wrap(content) {
    return `<div class="component companies-see-through-stat-chart-component">${content}</div>`
  }

  disconnect() {
    clearTimeout(this.flashTimeout)
    window.bootstrap.Modal.getInstance(this.modalTarget)?.dispose()
  }
}
